import { api } from "@/api";
import { env } from "@/config/env";
import type { AttachmentKind, InternalAttachment } from "./attachments";
import { formatClock, formatListTime } from "./format";
import type { PresenceStatus } from "./presence";
import type { InternalConversation, InternalMessage, InternalUser } from "./types";

// Mesmo padrão do WhatsApp: o baseURL do axios (env.API_URL) já inclui `/api`,
// então o path NÃO repete o prefixo `api/` — senão vira `/api/api/v2/...` (404).
const BASE = "v2/internal-chat";

/** Base absoluta para os streams SSE (consumidos via fetch-event-source). */
export const INTERNAL_SSE_BASE = `${env.API_URL}/${BASE}`;

const PAGE_SIZE_CONVERSATIONS = 15;
const PAGE_SIZE_MESSAGES = 20;
const PAGE_SIZE_USERS = 15;

// ---------------------------------------------------------------------------
// Shapes da API (snake_case no REST)
// ---------------------------------------------------------------------------

type ProfileAPI = {
  user_id: string;
  name: string;
  photo_url: string | null;
  status: PresenceStatus | null;
};

type ConversationAPI = {
  conversation_id: string;
  peer_user_id: string;
  peer_name: string;
  peer_photo_url: string | null;
  peer_status: PresenceStatus | null;
  last_message: string | null;
  last_message_from_me: boolean;
  last_message_at: string | null;
  unread_count: number;
};

type AttachmentAPI = {
  kind: AttachmentKind;
  name: string;
  size: number;
  mime: string;
  url: string;
};

type MessageAPI = {
  message_id: string;
  conversation_id: string;
  from_me: boolean;
  sender_id: string;
  message: string;
  send_at: string;
  status: string | null;
  attachment: AttachmentAPI | null;
};

/** Payload da mensagem no SSE da conversa (camelCase). */
export type SSEMessageAPI = {
  id: string;
  fromMe: boolean;
  senderId: string;
  content: string;
  status: string | null;
  createdAt: string;
  updatedAt: string;
  attachment: AttachmentAPI | null;
};

// ---------------------------------------------------------------------------
// Mappers → domínio (camelCase)
// ---------------------------------------------------------------------------

const mapProfile = (p: ProfileAPI): InternalUser => ({
  id: p.user_id,
  name: p.name,
  status: p.status ?? "offline",
  photoUrl: p.photo_url ?? undefined,
});

const mapConversation = (c: ConversationAPI): InternalConversation => ({
  id: c.conversation_id,
  user: {
    id: c.peer_user_id,
    name: c.peer_name,
    status: c.peer_status ?? "offline",
    photoUrl: c.peer_photo_url ?? undefined,
  },
  lastMessage: c.last_message,
  lastMessageAt: c.last_message_at ? formatListTime(c.last_message_at) : null,
  unreadCount: c.unread_count,
  lastFromMe: c.last_message_from_me,
});

let attachmentSeq = 0;
const mapAttachment = (a: AttachmentAPI): InternalAttachment => ({
  // A API não envia id no anexo; geramos um local apenas para keys/UI.
  id: `att-${Date.now()}-${attachmentSeq++}`,
  kind: a.kind,
  name: a.name,
  size: a.size,
  mime: a.mime,
  url: a.url,
});

const mapMessage = (m: MessageAPI): InternalMessage => ({
  id: m.message_id,
  text: m.message ?? "",
  fromMe: m.from_me,
  sentAt: formatClock(m.send_at),
  attachment: m.attachment ? mapAttachment(m.attachment) : undefined,
});

/** Mapeia a mensagem que chega pelo SSE (camelCase) para o domínio. */
export const mapSSEMessage = (m: SSEMessageAPI): InternalMessage => ({
  id: m.id,
  text: m.content ?? "",
  fromMe: m.fromMe,
  sentAt: formatClock(m.createdAt),
  attachment: m.attachment ? mapAttachment(m.attachment) : undefined,
});

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

export type SendMediaPayload = {
  base64: string;
  file_name: string;
  mime: string;
  caption?: string;
};

export const internalChatService = {
  // Perfil ---------------------------------------------------------------
  getMe: async (): Promise<InternalUser> => {
    const { data } = await api.get<ProfileAPI>(`${BASE}/me`);
    return mapProfile(data);
  },

  setStatus: async (status: PresenceStatus): Promise<void> => {
    await api.put(`${BASE}/me/status`, { status });
  },

  updatePhoto: async (base64: string, fileName: string, mime: string): Promise<string> => {
    const { data } = await api.put<{ photo_url: string }>(`${BASE}/me/photo`, {
      base64,
      file_name: fileName,
      mime,
    });
    return data.photo_url;
  },

  removePhoto: async (): Promise<void> => {
    await api.delete(`${BASE}/me/photo`);
  },

  // Diretório ------------------------------------------------------------
  getUsers: async (params: {
    search?: string;
    status?: PresenceStatus;
    page?: number;
  }): Promise<InternalUser[]> => {
    const { data } = await api.get<ProfileAPI[]>(`${BASE}/users`, {
      params: {
        search: params.search || undefined,
        status: params.status,
        page: params.page ?? 1,
        page_size: PAGE_SIZE_USERS,
      },
    });
    return data.map(mapProfile);
  },

  // Conversas ------------------------------------------------------------
  getConversations: async (params: {
    page: number;
    search?: string;
    unreadOnly?: boolean;
  }): Promise<InternalConversation[]> => {
    const { data } = await api.get<ConversationAPI[]>(`${BASE}/conversations`, {
      params: {
        page: params.page,
        search: params.search || undefined,
        unread_only: params.unreadOnly || undefined,
      },
    });
    return data.map(mapConversation);
  },

  getConversation: async (id: string): Promise<InternalConversation | null> => {
    try {
      const { data } = await api.get<ConversationAPI>(`${BASE}/conversations/${id}`);
      return mapConversation(data);
    } catch {
      return null; // 404 = inexistente / sem acesso
    }
  },

  openConversation: async (peerUserId: string): Promise<InternalConversation> => {
    const { data } = await api.post<ConversationAPI>(`${BASE}/conversations`, {
      peer_user_id: peerUserId,
    });
    return mapConversation(data);
  },

  // Mensagens ------------------------------------------------------------
  getMessages: async (
    conversationId: string,
    params: { page: number; before?: string },
  ): Promise<InternalMessage[]> => {
    const { data } = await api.get<MessageAPI[]>(
      `${BASE}/conversations/${conversationId}/messages`,
      { params: { page: params.page, before: params.before } },
    );
    return data.map(mapMessage);
  },

  sendMessage: async (conversationId: string, message: string): Promise<InternalMessage> => {
    const { data } = await api.post<MessageAPI>(
      `${BASE}/conversations/${conversationId}/messages`,
      { message },
    );
    return mapMessage(data);
  },

  sendMedia: async (
    conversationId: string,
    payload: SendMediaPayload,
  ): Promise<InternalMessage> => {
    const { data } = await api.post<MessageAPI>(
      `${BASE}/conversations/${conversationId}/messages/media`,
      payload,
    );
    return mapMessage(data);
  },

  markRead: async (conversationId: string): Promise<void> => {
    await api.put(`${BASE}/conversations/${conversationId}/read`);
  },

  markUnread: async (conversationId: string): Promise<void> => {
    await api.put(`${BASE}/conversations/${conversationId}/unread`);
  },
};

export const INTERNAL_PAGE_SIZE = {
  conversations: PAGE_SIZE_CONVERSATIONS,
  messages: PAGE_SIZE_MESSAGES,
  users: PAGE_SIZE_USERS,
};
